import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import ErrorText from "@/components/universal/error-text";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { getGetPayorQueryOptions } from "@/requests/gen/react-query/payor";
import { useSuspenseQuery } from "@tanstack/react-query";
import { suspenseOptions } from "@/lib/suspense-query";
import { Group, Hash, Landmark, Mail, Phone } from "lucide-react";
import { PayorCrumbs } from "../-crumbs";
import parsePhoneNumber from "libphonenumber-js";
import numericId from "@/lib/numeric-id";

export const Route = createFileRoute("/_private/_admin/payor/$payorId/")({
  loader: ({ context: { queryClient }, params: { payorId } }) =>
    queryClient.ensureQueryData(getGetPayorQueryOptions(numericId(payorId))),
  component: PayorDetailsPage,
});

function PayorDetailsPage() {
  const { payorId } = Route.useParams();
  const { data: payor, error } = useSuspenseQuery(
    suspenseOptions(getGetPayorQueryOptions(numericId(payorId))),
  );

  return (
    <div>
      <title>Payor Details - Legrande Health Portal</title>
      <PayorCrumbs />
      {/* Payor Details Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            Payor Details
            {error && <ErrorText text="Failed to load payor details" />}
          </CardTitle>
          <CardDescription>Review the payor details below.</CardDescription>
        </CardHeader>
        <CardContent>
          {payor && (
            <>
              <div>
                <h4 className="text-xl font-bold mb-4">{payor.name}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2  gap-4">
                  {/* Col 1 */}
                  <div>
                    <p className="flex items-center gap-2 font-semibold">
                      <Mail className="inline size-5" />
                      Email
                    </p>
                    <p className="ml-8 text-muted-foreground mb-4">
                      {payor.email}
                    </p>

                    <p className="flex items-center gap-2 font-semibold">
                      <Phone className="inline size-5" />
                      Phone
                    </p>
                    <p className="ml-8 text-muted-foreground mb-4">
                      {parsePhoneNumber(payor.phone ?? "")?.formatNational()}
                    </p>

                    <p className="flex items-center gap-2 font-semibold">
                      <Group className="inline size-5" />
                      Category
                    </p>
                    <p className="ml-8 text-muted-foreground mb-4">
                      {payor.payor_type}
                    </p>
                  </div>

                  {/* Col 2 */}
                  <div>
                    <p className="flex items-center gap-2 font-semibold">
                      <Landmark className="inline size-5" />
                      BIN Number
                    </p>
                    <p className="ml-8 text-muted-foreground mb-4">
                      {payor.bin_number}
                    </p>

                    <p className="flex items-center gap-2 font-semibold">
                      <Hash className="inline size-5" />
                      PCN
                    </p>
                    <p className="ml-8 text-muted-foreground mb-4">
                      {payor.pcn}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 justify-end">
                <Button className="w-20" asChild variant="outline">
                  <Link to="/payor/lookup">Back</Link>
                </Button>
                <Button className="w-20" asChild>
                  <Link to="/payor/$payorId/edit" params={{ payorId }}>
                    Edit
                  </Link>
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
